import { computed, inject, Injectable } from "@angular/core";
import { ControleApiService } from "./controle-api.service";
import { ControleState, ControleStoreService } from "./controle-store.service";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Controle } from "../../models/controle.model";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { firstValueFrom } from "rxjs";
import { ChecklistFacadeService } from "../../../list/services/store/checklist-facade.service";
import { ElementTypeControle } from "../../enums/element-type-controle";

@Injectable({ providedIn: "root" })
export class ControleFacadeService extends EntityFacadeService<
  Controle,
  ControleApiService,
  ControleState,
  EntityMetadata,
  ControleStoreService
> {
  checklistFacade = inject(ChecklistFacadeService);

  constructor() {
    const api = inject(ControleApiService);
    const store = inject(ControleStoreService);
    super(api, store);
    store.initStatus(ControleFacadeService);
  }

  //todo yoann passer en computed
  getByPodId(podId: string) {
    return this.elements()
      .filter((e) => e()?.podId === podId)
      .map((e) => e());
  }

  controlesGeneraux = computed(() => {
    return this.elements()
      .map((e) => e())
      .filter((e) => {
        return (
          [
            ElementTypeControle.START_OPE_GENERAUX,
            ElementTypeControle.END_OPE_GENERAUX,
            ElementTypeControle.LOT_RECEPTION_GENERAUX,
            ElementTypeControle.PREPARATION_EXPE_GENERAUX,
            ElementTypeControle.DECHARGEMENT_GENERAUX,
            ElementTypeControle.DECHARGEMENT_ARTICLE_GENERAUX,
            ElementTypeControle.EXPEDITION_GENERAUX,
            ElementTypeControle.LIBERATOIRE_GENERAUX,
            ElementTypeControle.CHARGEMENT_GENERAUX,
            ElementTypeControle.CHARGEMENT_ARTICLE_GENERAUX,
            ElementTypeControle.CHARGEMENT_UNITE_GENERAUX,
            ElementTypeControle.PRODUCTION_GENERAUX,
          ].includes(e.elementType) && e.model
        );
      });
  });

  getControlesByType(type: ElementTypeControle, isModel = true) {
    return this.elements()
      .map((e) => e())
      .filter((e) => e.elementType === type && e.model === isModel);
  }

  @UpdateStatus
  async loadForPodId(podId: string) {
    try {
      const controles = await firstValueFrom(this.api.loadForPodId(podId));
      controles.forEach((c) => {
        this.store.upsertOne(c);
      });
      return controles;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  @UpdateStatus
  async loadForPodIds(podIds: string[]) {
    try {
      const controles = await firstValueFrom(this.api.loadForPodIds(podIds));
      controles.forEach((c) => {
        this.store.upsertOne(c);
      });
      return controles;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  @Untrack
  @UpdateStatus
  async duplicate(controle: Controle) {
    const saved = await firstValueFrom(this.api.duplicate(controle));
    this.store.upsertOne(saved);
    return saved;
  }

  @Untrack
  @UpdateStatus
  async answer(controle: Controle) {
    const saved = await firstValueFrom(this.api.answer(controle));
    this.store.upsertOne(saved);
    return saved;
  }

  @Untrack
  @UpdateStatus
  async answerForChecklist(controle: Controle, checklistId: string) {
    const saved = await firstValueFrom(
      this.api.answerForChecklist(controle, checklistId),
    );
    this.store.upsertOne(saved.controle);
    this.checklistFacade.store.upsertOne(saved.checklist);
    return saved;
  }

  @Untrack
  @UpdateStatus
  async loadControlesGeneraux(elemTypeControle?: ElementTypeControle) {
    const controles = await firstValueFrom(
      this.api.loadControlesGeneraux(elemTypeControle),
    );
    this.store.addMany(controles);
  }

  @Untrack
  @UpdateStatus
  async upsertAll(payload: { controles: Controle[] }) {
    const saved = await firstValueFrom(this.api.saveAll(payload));
    saved.forEach((saved: Controle) => {
      this.store.upsertOne(saved);
    });
    return saved;
  }

  @Untrack
  @UpdateStatus
  async createPod() {
    return await firstValueFrom(this.api.createPod());
  }
}
